import { Avatar, Button, Stack, TextField, Typography } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { useActiveAddress, useConnection } from "arweave-wallet-kit"
import { useState } from "react"

import type { Pool } from "@/api/pools-api"
import { getBalance } from "@/api/token-api"
import { lockTokens } from "@/api/tokenlocker-api"
import { IdBlock } from "@/components/IdBlock"
import { useTokenInfo } from "@/hooks/use-token-info"
import { parseBigIntAsNumber, parseNumberAsBigInt, formatNumberAuto } from "@/utils/format"

interface LockProps {
  pool: Pool
}

export function Lock({ pool }: LockProps) {
  const activeAddr = useActiveAddress()
  const { connect } = useConnection()

  const [lockAmount, setLockAmount] = useState("0")
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [receipt, setReceipt] = useState<string | null>(null)
  const [backendError, setBackendError] = useState<Error | null>(null)
  const [period, setPeriod] = useState(30)

  const { data: lpBalance = "0", refetch: refetchBalance } = useQuery({
    queryKey: ["lpBalance", activeAddr, pool.id],
    enabled: !!activeAddr,
    queryFn: async () => {
      if (!activeAddr) return "0"
      return (await getBalance(pool.id, activeAddr)).toString()
    },
  })

  const [lpToken] = useTokenInfo(pool.id)

  const handleLock = async () => {
    if (!activeAddr || !lpToken) return connect()

    const normalized = parseNumberAsBigInt(lockAmount, lpToken?.denomination)
    const newErrors: Record<string, string> = {}

    if (!lockAmount || normalized === 0n) newErrors.lockAmount = "Required"
    if (normalized > BigInt(lpBalance)) newErrors.lockAmount = "Insufficient balance"

    if (!period || period < 1) newErrors.period = "Invalid period"

    if (Object.keys(newErrors).length) {
      setErrors(newErrors)
      return
    }

    setLoading(true)
    setErrors({})
    setBackendError(null)

    try {
      const result = await lockTokens({
        tokenProcess: pool.id,
        amount: normalized,
        periodMs: period * 24 * 60 * 60 * 1000,
      })
      setReceipt(result)
      setLockAmount("0")
    } catch (err: any) {
      setBackendError(err)
    } finally {
      setLoading(false)
      refetchBalance()
    }
  }

  if (!lpToken) return null
  return (
    <Stack gap={4} sx={{ paddingTop: 2 }}>
      {!receipt ? (
        <>
          <Stack gap={0.5}>
            <TextField
              id="fromAmount"
              fullWidth
              label={
                <Stack direction="row" gap={1}>
                  <Avatar
                    src={`https://arweave.net/${lpToken.logo}`}
                    alt={lpToken.name}
                    sx={{ width: 24, height: 24 }}
                  />
                  {lpToken.ticker}
                  <span>
                    ({formatNumberAuto(parseBigIntAsNumber(lpBalance, lpToken.denomination))})
                  </span>
                </Stack>
              }
              type="number"
              disabled={loading}
              value={lockAmount}
              onChange={(e) => setLockAmount(e.target.value)}
              onFocus={(e) => {
                if (e.target.value === "0") setLockAmount("")
              }}
              error={!!errors.lockAmount}
              helperText={errors.lockAmount}
            />
            <Stack direction="row" gap={0.5} alignItems="center" justifyContent="space-between">
              <Button
                disabled={loading}
                onClick={() =>
                  setLockAmount(
                    String(
                      parseFloat(parseBigIntAsNumber(lpBalance, lpToken.denomination)) * 0.001,
                    ),
                  )
                }
              >
                0.1
              </Button>
              <Button
                disabled={loading}
                onClick={() =>
                  setLockAmount(
                    String(parseFloat(parseBigIntAsNumber(lpBalance, lpToken.denomination)) * 0.01),
                  )
                }
              >
                1
              </Button>
              <Button
                disabled={loading}
                onClick={() =>
                  setLockAmount(
                    String(parseFloat(parseBigIntAsNumber(lpBalance, lpToken.denomination)) * 0.1),
                  )
                }
              >
                10
              </Button>
              <Button
                disabled={loading}
                onClick={() => setLockAmount(parseBigIntAsNumber(lpBalance, lpToken.denomination))}
              >
                100
              </Button>
            </Stack>
          </Stack>

          <Stack gap={0.5}>
            <TextField
              label="Period (days)"
              type="number"
              disabled={loading}
              value={period}
              onChange={(e) => setPeriod(parseInt(e.target.value))}
              error={!!errors.period}
              helperText={errors.period}
            />
            <Stack direction="row" gap={0.5} alignItems="center" justifyContent="space-between">
              <Button onClick={() => setPeriod(30)}>30</Button>
              <Button onClick={() => setPeriod(60)}>60</Button>
              <Button onClick={() => setPeriod(90)}>90</Button>
              <Button onClick={() => setPeriod(365)}>365</Button>
            </Stack>
          </Stack>

          <Button
            variant="contained"
            onClick={activeAddr ? handleLock : connect}
            disabled={activeAddr ? !lockAmount || lockAmount === "0" || loading : false}
          >
            {activeAddr ? (loading ? "Locking..." : "Lock") : "Connect Wallet"}
          </Button>
        </>
      ) : (
        <Stack alignItems="center" gap={1}>
          <Typography variant="subtitle2" color="green">
            Successfully locked!
          </Typography>
          <IdBlock
            label={"View on ao.link"}
            value={receipt}
            href={`https://ao.link/#/message/${receipt}`}
            hideCopyToClipboard
          />{" "}
          <Button
            sx={{ marginTop: 4 }}
            variant="contained"
            onClick={() => {
              setReceipt(null)
              setLockAmount("0")
            }}
          >
            Lock again
          </Button>
        </Stack>
      )}

      {backendError && (
        <Typography color="error" textAlign="center">
          {backendError.message || "An error occurred"}
        </Typography>
      )}
    </Stack>
  )
}
